<template>
    <el-dialog v-model="visible" width="70%" :before-close="handleClose" destroy-on-close center>
        <template #title>
            <div class="dialog-header">
                <div class="header-left">
                    <!-- 左侧占位，保持标题居中 -->
                </div>
                <div class="header-center">
                    <span>文件预览</span>
                </div>
                <div class="header-right">
                    <el-button v-if="isPdf && !isFullscreen && previewUrl" type="primary" size="small"
                        @click="toggleFullscreen" class="fullscreen-btn">
                        <i class="el-icon-full-screen"></i>
                        全屏预览
                    </el-button>
                </div>
            </div>
        </template>
        <div v-loading="loading" class="preview-container">
            <div v-if="error" class="error-message">
                <el-alert :title="error" type="error" show-icon :closable="false" />
            </div>

            <div v-else-if="previewUrl" class="preview-content">
                <!-- 图片预览 -->
                <div v-if="isImage" class="image-preview">
                    <img :src="previewUrl" class="preview-image" alt="图片预览">
                    <div class="image-tips">
                        <i class="el-icon-picture-outline"></i>
                        图片预览
                    </div>
                </div>

                <!-- PDF预览 -->
                <div v-else-if="isPdf" class="pdf-preview">
                    <div class="pdf-container" :class="{ fullscreen: isFullscreen }">
                        <!-- 全屏时的工具栏 -->
                        <div v-if="isFullscreen" class="pdf-toolbar-fullscreen">
                            <el-button type="primary" size="small" @click="exitFullscreen">
                                <i class="el-icon-full-screen"></i>
                                退出全屏
                            </el-button>
                            <el-button type="default" size="small" @click="exitFullscreen">
                                <i class="el-icon-close"></i>
                                关闭
                            </el-button>
                        </div>

                        <iframe ref="pdfIframe" :src="previewUrl" title="PDF预览" class="pdf-iframe" />
                    </div>
                    <div v-if="!isFullscreen" class="pdf-tips">
                        <i class="el-icon-document"></i>
                        PDF文档预览 - 标题栏的全屏按钮可获得更好的阅读体验
                    </div>
                </div>

                <!-- 不支持的文件类型 -->
                <div v-else class="unsupported-file">
                    <el-result icon="warning" title="不支持预览此文件类型" sub-title="当前仅支持图片和PDF文件预览">
                        <template #extra>
                            <el-button type="primary" @click="downloadFile">
                                下载文件
                            </el-button>
                        </template>
                    </el-result>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { ref, computed, watch, nextTick } from "vue"
import api from "../../../../axios/api"
import { ElMessage } from "element-plus"

export default {
    name: "FilePreview",
    props: {
        modelValue: {
            type: Boolean,
            default: false
        },
        fileUri: {
            type: String,
            default: ""
        }
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        const loading = ref(false)
        const previewUrl = ref("")
        const error = ref("")
        const isFullscreen = ref(false)
        const pdfIframe = ref(null)

        const visible = computed({
            get: () => props.modelValue,
            set: value => emit("update:modelValue", value)
        })

        // 判断文件类型
        const fileExtension = computed(() => {
            if (!props.fileUri) return ""
            const parts = props.fileUri.split(".")
            return parts[parts.length - 1]?.toLowerCase() || ""
        })

        const isImage = computed(() => {
            const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp"]
            return imageExts.includes(fileExtension.value)
        })

        const isPdf = computed(() => {
            return fileExtension.value === "pdf"
        })

        // 获取文件预览URL
        const getPreviewUrl = async () => {
            if (!props.fileUri) return

            loading.value = true
            error.value = ""
            previewUrl.value = ""

            try {
                const res = await api.S3down({ key: props.fileUri })
                if (res.data.code === 200) {
                    previewUrl.value = res.data.data.get_url
                } else {
                    error.value = res.data.msg || "获取文件预览地址失败"
                }
            } catch (err) {
                console.error("获取文件预览地址失败:", err)
                error.value = "获取文件预览地址失败"
            } finally {
                loading.value = false
            }
        }

        // 下载文件
        const downloadFile = () => {
            if (previewUrl.value) {
                const link = document.createElement("a")
                link.href = previewUrl.value
                link.download = props.fileUri.split("/").pop() || "file"
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            }
        }

        // 全屏切换
        const toggleFullscreen = async () => {
            if (!isFullscreen.value) {
                // 进入全屏
                try {
                    const container = document.querySelector(".pdf-container")
                    if (container) {
                        if (container.requestFullscreen) {
                            await container.requestFullscreen()
                        } else if (container.webkitRequestFullscreen) {
                            await container.webkitRequestFullscreen()
                        } else if (container.msRequestFullscreen) {
                            await container.msRequestFullscreen()
                        }
                        isFullscreen.value = true
                    }
                } catch (err) {
                    console.error("进入全屏失败:", err)
                    ElMessage.error("您的浏览器不支持全屏功能")
                }
            } else {
                exitFullscreen()
            }
        }

        // 退出全屏
        const exitFullscreen = () => {
            if (document.exitFullscreen) {
                document.exitFullscreen()
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen()
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen()
            }
            isFullscreen.value = false
        }

        // 监听全屏状态变化
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            )

            if (!isCurrentlyFullscreen && isFullscreen.value) {
                isFullscreen.value = false
            }
        }

        // 关闭弹窗
        const handleClose = () => {
            if (isFullscreen.value) {
                exitFullscreen()
            }
            visible.value = false
            previewUrl.value = ""
            error.value = ""
        }

        // 监听弹窗打开
        watch(
            () => props.modelValue,
            newVal => {
                if (newVal && props.fileUri) {
                    getPreviewUrl()
                    // 添加全屏事件监听
                    nextTick(() => {
                        document.addEventListener("fullscreenchange", handleFullscreenChange)
                        document.addEventListener(
                            "webkitfullscreenchange",
                            handleFullscreenChange
                        )
                        document.addEventListener("msfullscreenchange", handleFullscreenChange)
                    })
                } else {
                    // 移除全屏事件监听
                    document.removeEventListener("fullscreenchange", handleFullscreenChange)
                    document.removeEventListener(
                        "webkitfullscreenchange",
                        handleFullscreenChange
                    )
                    document.removeEventListener(
                        "msfullscreenchange",
                        handleFullscreenChange
                    )
                    isFullscreen.value = false
                }
            }
        )

        return {
            visible,
            loading,
            previewUrl,
            error,
            isFullscreen,
            pdfIframe,
            isImage,
            isPdf,
            downloadFile,
            toggleFullscreen,
            exitFullscreen,
            handleClose
        }
    }
}
</script>

<style scoped lang="stylus" rel="stylesheet/stylus">
// 自定义弹窗标题栏样式
.dialog-header
  display flex
  align-items center
  width 100%
  height 100%

  .header-left,
  .header-right
    flex 1
    display flex
    align-items center

  .header-left
    justify-content flex-start

  .header-center
    flex 1
    display flex
    align-items center
    justify-content center

    span
      font-size 16px
      font-weight 500
      color #303133

  .header-right
    justify-content flex-end
    margin-right 40px  // 添加右侧边距，避免与关闭按钮重叠

    .fullscreen-btn
      position relative
      z-index 10  // 确保按钮在最上层

.preview-container
  min-height 200px

  .error-message
    padding 20px

  .preview-content
    .image-preview
      text-align center
      padding 20px

      .preview-image
        max-width 100%
        max-height 60vh
        border-radius 8px
        box-shadow 0 4px 12px rgba(0, 0, 0, 0.1)
        cursor pointer
        transition transform 0.2s ease

        &:hover
          transform scale(1.02)

      .image-tips
        margin-top 16px
        color #909399
        font-size 14px
        display flex
        align-items center
        justify-content center
        gap 6px

        i
          font-size 16px

      .image-error
        display flex
        flex-direction column
        align-items center
        gap 8px
        padding 40px
        color #909399

        i
          font-size 48px

    .pdf-preview
      text-align center
      padding 20px

      .pdf-container
        position relative

        &.fullscreen
          position fixed
          top 0
          left 0
          width 100vw !important
          height 100vh !important
          background #fff
          z-index 9999
          padding 0

          .pdf-iframe
            width 100vw !important
            height 100vh !important
            border-radius 0 !important
            box-shadow none !important

      // 全屏时的工具栏 - 左下角
      .pdf-toolbar-fullscreen
        position fixed
        bottom 30px
        left 30px
        z-index 10000
        display flex
        gap 10px
        background rgba(0, 0, 0, 0.8)
        backdrop-filter blur(10px)
        padding 12px 16px
        border-radius 8px
        box-shadow 0 4px 20px rgba(0, 0, 0, 0.3)

      .pdf-iframe
        width 100%
        height 60vh
        border none
        border-radius 8px
        box-shadow 0 4px 12px rgba(0, 0, 0, 0.1)
        transition all 0.3s ease

      .pdf-tips
        margin-top 16px
        color #909399
        font-size 14px
        display flex
        align-items center
        justify-content center
        gap 6px

        i
          font-size 16px

    .unsupported-file
      padding 40px 20px

// 全屏时的全局样式
@keyframes slideInUp
  from
    transform translateY(20px)
    opacity 0
  to
    transform translateY(0)
    opacity 1
</style>
